import { PlusIcon } from "@xola/icons";
import { Button, ButtonGroup } from "@xola/ui-kit";
import clsx from "clsx";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Affiliate, AffiliateStatus, Permission } from "types";
import { IsGranted } from "../Common/IsGranted";
import { SearchInput } from "../Common/SearchInput";
import { TableSkeleton } from "../Common/Skeleton/TableSkeleton";
import { ContentLayout } from "../Layout/ContentLayout";
import { AgentTable } from "./AgentTable";
import { ApplyTermsTemplateModal } from "./TermsTemplate/ApplyTermsTemplateModal";
import { useUser } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { isAffiliate } from "@/models/User";
import { sortBy } from "@/utils/locale";

const enum AgentFilter {
    ALL = 0,
    ACTIVE = 1,
}

export const AgentList = () => {
    const user = useUser();
    const history = useHistory();
    const [currentFilter, setCurrentFilter] = useState(AgentFilter.ACTIVE);
    const [selectedAgents, setSelectedAgents] = useState<ID[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isApplyTermsTemplateModalOpen, setIsApplyTermsTemplateModalOpen] = useState(false);
    const { data: agents, isLoading } = useAutoFetchCollection<Affiliate>(["affiliates"]);

    const { isGranted } = useAuthorization();

    // Sort agents by status
    const [sortedAgents, setSortedAgents] = useState<Affiliate[]>([]);

    const upsertAgent = (agent: Affiliate) => {
        const index = sortedAgents.findIndex((p) => p.id === agent.id);
        if (index === -1) {
            // If the agent doesn't exist in the array, add it to the end
            setSortedAgents([...sortedAgents, agent]);
        } else {
            // If the agent exists in the array, replace it with the updated agent
            const updatedAgents = produce(sortedAgents, (draft) => {
                draft[index] = agent;
            });
            setSortedAgents(updatedAgents);
        }
    };

    useEffect(() => {
        setSortedAgents((currentSorted) => {
            if (currentSorted.length === 0) {
                // If sortedAgents is empty, sort the entire agents array
                return sortBy([...agents], "status");
            }

            // If sortedAgents is not empty, sort only the new batch of agents
            const newBatch = agents.slice(currentSorted.length);
            const sortedNewBatch = sortBy(newBatch, "status");
            return [...currentSorted, ...sortedNewBatch];
        });
    }, [agents]);

    const filteredAgents = useMemo(() => {
        // Apply Status Filter
        const agentsFilter =
            currentFilter === AgentFilter.ALL
                ? sortedAgents
                : sortedAgents.filter((agent) => agent.status === AffiliateStatus.ACTIVE);

        // Build a Map of Agents by ID for Quick Lookup
        const agentsById = new Map();
        for (const agent of agentsFilter) {
            agentsById.set(agent.id, agent);
        }

        // Identify Agents Matching the Search Term
        const agentIdsToInclude = new Set();
        if (searchTerm.trim()) {
            const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();
            for (const agent of agentsFilter) {
                if (agent.name.toLowerCase().includes(lowerCaseSearchTerm)) {
                    agentIdsToInclude.add(agent.id);
                    if (agent.children) {
                        for (const childAgentId of agent.children) {
                            agentIdsToInclude.add(childAgentId);
                        }
                    }
                }
            }
        } else {
            // Include all agents after status filter
            for (const agent of agentsFilter) {
                agentIdsToInclude.add(agent.id);
            }
        }

        // Include Parents of Matched Agents
        const addParentAgents = (agentId: string) => {
            const agent = agentsById.get(agentId);
            if (agent?.parent && !agentIdsToInclude.has(agent.parent)) {
                agentIdsToInclude.add(agent.parent);
                addParentAgents(agent.parent);
            }
        };

        // Process each agent to include their parents
        for (const agentId of agentIdsToInclude) {
            addParentAgents(agentId as string);
        }

        // Build the Final Filtered Agents List
        return agentsFilter.filter((agent) => agentIdsToInclude.has(agent.id));
    }, [currentFilter, sortedAgents, searchTerm]);

    const loggedInAffiliate = agents.find((agent) => agent?.user?.id === user.id);

    const hasActiveNonChildOrLoggedInAgents = useMemo(() => {
        return agents.some(
            (agent) => agent.status === AffiliateStatus.ACTIVE && (!agent.parent || agent.id === loggedInAffiliate?.id),
        );
    }, [agents, loggedInAffiliate]);

    // The previous was too heavy for the first load as it grouping all the agents
    const showTable = currentFilter === AgentFilter.ALL || hasActiveNonChildOrLoggedInAgents;

    // Restrict access for logged in affiliate
    const allowCreate = isAffiliate(user) ? loggedInAffiliate?.allowChildren : isGranted(Permission.MODIFY_PARTNERS);

    const handleAgentFilterChange = (value: number) => {
        setCurrentFilter(value);
        setSelectedAgents([]);
    };

    const handleAgentSelection = (selectedAgents: string[]) => {
        setSelectedAgents(selectedAgents);
    };

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    return (
        <ContentLayout isWide className="space-y-4">
            <div className="flex w-full items-center">
                <div className="flex flex-grow items-center space-x-4">
                    <h2 className="flex-shrink-0 whitespace-nowrap leading-tight tracking-tight">Manage Agents</h2>
                    <div className="flex-grow pr-4">
                        {agents.length > 0 && !loggedInAffiliate && <SearchInput onChange={handleSearchChange} />}
                    </div>
                </div>
                <div className={clsx("flex items-center space-x-2", { "pointer-events-none": isLoading })}>
                    <IsGranted permissions={Permission.MODIFY_PARTNERS}>
                        <Button
                            disabled={selectedAgents.length === 0}
                            onClick={() => setIsApplyTermsTemplateModalOpen(true)}
                        >
                            Apply Terms Template
                        </Button>
                    </IsGranted>

                    {agents.length > 0 && !loggedInAffiliate && (
                        <AgentListFilter value={currentFilter} onChange={handleAgentFilterChange} />
                    )}

                    {allowCreate ? (
                        <Button icon={<PlusIcon />} onClick={() => history.push("/distribution/agents/create")}>
                            Create Agent
                        </Button>
                    ) : null}
                </div>
            </div>

            <div className="w-full">
                {isLoading ? (
                    <TableSkeleton rows={7} columns={5} />
                ) : filteredAgents?.length > 0 && showTable ? (
                    <AgentTable
                        agents={filteredAgents}
                        onLinkClick={(route) => history.push(route)}
                        onSelectAgents={handleAgentSelection}
                        onArchiveAgent={upsertAgent}
                    />
                ) : (
                    <span className="flex items-center justify-center pt-25" data-testid="agent-list-no-agents-label">
                        No Agents found. Click the button above to create a new agent
                    </span>
                )}
            </div>

            <ApplyTermsTemplateModal
                isOpen={isApplyTermsTemplateModalOpen}
                agents={selectedAgents}
                onClose={() => setIsApplyTermsTemplateModalOpen(false)}
            />
        </ContentLayout>
    );
};

interface AgentListFilterProps {
    value: number;
    onChange: (value: number) => void;
}

const AgentListFilter = ({ value, onChange }: AgentListFilterProps) => {
    return (
        <ButtonGroup size="medium" value={value} onChange={onChange}>
            <ButtonGroup.Button>All</ButtonGroup.Button>
            <ButtonGroup.Button>Active</ButtonGroup.Button>
        </ButtonGroup>
    );
};
